"use client";

import { useCallback, useMemo, useState } from "react";
import classnames from "classnames";

import { SegmentedControl } from "../segmented-control/segmented-control";
import { Button } from "../button/button";
import { SatelliteIcon } from "../sidebar/satellite-icon";
import { getBackendOverride } from "./config";
import styles from "./backend-settings.module.scss";

export interface ServiceDefinition {
  key: string;
  name: string;
  prodUrl: string;
}

export interface EnvironmentPreset {
  key: string;
  name: string;
  urls: Record<string, string>;
}

export interface BackendSettingsProps {
  services: ServiceDefinition[];
  presets: EnvironmentPreset[];
  onApply: (overrides: Record<string, string>) => void;
}

function detectActivePreset(
  services: ServiceDefinition[],
  presets: EnvironmentPreset[],
  currentUrls: Record<string, string>,
): string {
  for (const preset of presets) {
    const matches = services.every((s) => currentUrls[s.key] === preset.urls[s.key]);
    if (matches) return preset.key;
  }
  return "custom";
}

export function BackendSettings({ services, presets, onApply }: BackendSettingsProps) {
  // Current effective URLs (override ?? prodUrl)
  const initialUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    for (const service of services) {
      urls[service.key] = getBackendOverride(service.key) ?? service.prodUrl;
    }
    return urls;
  }, [services]);

  const [editUrls, setEditUrls] = useState<Record<string, string>>(initialUrls);

  const activePreset = useMemo(
    () => detectActivePreset(services, presets, initialUrls),
    [services, presets, initialUrls],
  );

  const selectedPreset = useMemo(
    () => detectActivePreset(services, presets, editUrls),
    [services, presets, editUrls],
  );

  const presetOptions = useMemo(
    () => [
      ...presets.map((p) => ({ value: p.key, label: p.name })),
      ...(selectedPreset === "custom" ? [{ value: "custom" as string, label: "Custom" }] : []),
    ],
    [presets, selectedPreset],
  );

  const handlePresetChange = useCallback(
    (presetKey: string) => {
      if (presetKey === "custom") return;
      const preset = presets.find((p) => p.key === presetKey);
      if (!preset) return;
      setEditUrls({ ...preset.urls });
    },
    [presets],
  );

  const handleUrlChange = useCallback((serviceKey: string, url: string) => {
    setEditUrls((prev) => ({ ...prev, [serviceKey]: url }));
  }, []);

  const hasChanges = useMemo(() => {
    return services.some((s) => editUrls[s.key] !== initialUrls[s.key]);
  }, [services, editUrls, initialUrls]);

  const handleApply = useCallback(() => {
    const overrides: Record<string, string> = {};
    for (const service of services) {
      overrides[service.key] = editUrls[service.key];
    }
    onApply(overrides);
  }, [services, editUrls, onApply]);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerIcon}>
            <SatelliteIcon size={24} />
          </div>
          <div>
            <h1 className={styles.title}>Backend Services</h1>
            <p className={styles.subtitle}>
              Currently using{" "}
              <strong>
                {activePreset === "custom"
                  ? "custom"
                  : presets.find((p) => p.key === activePreset)?.name}
              </strong>{" "}
              backend
            </p>
          </div>
        </header>

        <section className={styles.section}>
          <label className={styles.label}>Environment</label>
          <SegmentedControl
            options={presetOptions}
            value={selectedPreset}
            onChange={handlePresetChange}
          />
        </section>

        <section className={styles.section}>
          <label className={styles.label}>Service URLs</label>
          <div className={styles.serviceList}>
            {services.map((service) => {
              const isDefault = editUrls[service.key] === service.prodUrl;
              return (
                <div key={service.key} className={styles.serviceRow}>
                  <div className={styles.serviceName}>
                    <span>{service.name}</span>
                    {isDefault && <span className={styles.defaultBadge}>default</span>}
                  </div>
                  <input
                    type="url"
                    className={classnames(styles.urlInput, !isDefault && styles.urlInputOverridden)}
                    value={editUrls[service.key]}
                    onChange={(e) => handleUrlChange(service.key, e.target.value)}
                    placeholder={service.prodUrl}
                  />
                </div>
              );
            })}
          </div>
        </section>

        <div className={styles.warning}>
          Changing backend will sign you out and reload the page.
        </div>

        <div className={styles.actions}>
          <Button onClick={handleApply} disabled={!hasChanges}>
            Apply Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
